// Section for import npm components -----------------------------------------------------------------------------------
import React, { useContext, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import camelcaseKeys from 'camelcase-keys';

// MUI components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

// Section for import custom components --------------------------------------------------------------------------------
import { AppConfigContext } from '@providers/AppConfigProvider';
import RentMarketPriceHistoryProvider from '@providers/MansionDetail/RentMarketPriceHistory/RentMarketPriceHistoryProvider';

import SquareMetersSelector from '@components/MansionDetail/RentMarketPriceHistory/SquareMetersSelector';
import Chart from '@components/MansionDetail/RentMarketPriceHistory/Chart';
import ChartSP from '@components/MansionDetail/RentMarketPriceHistory/SP/Chart';
import CustomTabs from '@components/MansionDetail/RentMarketPriceHistory/CustomTabs';
import CustomTabPanel from '@components/MansionDetail/RentMarketPriceHistory/CustomTabPanel';

// Section for type definition -----------------------------------------------------------------------------------------
import RentMarketPriceHistoryPropsData, {
  RentMarketPriceHistoryContextType,
} from '@/types/components/MansionDetail/RentMarketPriceHistoryPropsData';

type RentMarketPriceHistoryProps = {
  // eslint-disable-next-line react/require-default-props
  mansionId?: number;
  rentMarketPriceHistory: RentMarketPriceHistoryPropsData;
};

export default function RentMarketPriceHistory(props: RentMarketPriceHistoryProps) {
  const { isMobile, pathRoot } = useContext(AppConfigContext);
  const { mansionId, rentMarketPriceHistory } = props;
  const isRentMarketPriceHistory = rentMarketPriceHistory !== false;
  // console.log(rentMarketPriceHistory);
  // @ts-ignore
  const averageSquareMeterValue: number = isRentMarketPriceHistory ? rentMarketPriceHistory.squareMeterAverage : 0;
  const initialSelectedSquareMeter: number = 70;

  const [rangeOfYearValue, setRangeOfYear] = useState(1);
  const rentMarketPriceHistoryContext: RentMarketPriceHistoryContextType = {
    RentMarketPriceHistoryData: rentMarketPriceHistory,
    averageSquareMeter: averageSquareMeterValue,
    selectedSquareMeter: initialSelectedSquareMeter,
    rangeOfYear: rangeOfYearValue,
  };

  const [mansionRentMarketPriceHistory, setMansionRentMarketPriceHistoryState] =
    useState(rentMarketPriceHistoryContext);

  const [tabValue, setTabValue] = useState(0);
  const [legendState, setLegendState] = useState({ series1: true, series2: true, series3: true, series4: true });

  function convertDateKey(keyName: string): string {
    const year = keyName.slice(0, 4);
    const month = keyName.slice(4, 6);
    const day = keyName.slice(6, 8);
    return `${year}-${month}-${day}`;
  }

  function changeDateKeyName(data: any) {
    if (!data) {
      return false;
    }
    const convertObject: { [key: string]: any } = {};
    Object.keys(data).forEach((key) => {
      const newKey = convertDateKey(key);
      convertObject[newKey] = data[key];
    });
    return convertObject;
  }

  function convertApiData(apiJson: any): RentMarketPriceHistoryPropsData {
    const apiResults = apiJson.results;
    // console.log(apiResults, 'apiResults:');
    let convertObject = {};

    const marketPriceHistoryCitiesHistories = changeDateKeyName(
      apiResults.marketPriceHistoryCities.marketPriceHistories,
    );
    const marketPriceHistoryMansionsHistories = changeDateKeyName(
      apiResults.marketPriceHistoryMansions.marketPriceHistories,
    );
    const marketPriceHistoryStationsHistories = changeDateKeyName(
      apiResults.marketPriceHistoryStations.marketPriceHistories,
    );
    const marketPriceHistoryTownsHistories = changeDateKeyName(apiResults.marketPriceHistoryTowns.marketPriceHistories);

    convertObject = {
      squareMeterAverage: apiResults.squareMeterAverage,
      marketPriceHistoryCities:
        marketPriceHistoryCitiesHistories !== false
          ? {
              cityName: apiResults.marketPriceHistoryCities.cityName,
              marketPriceHistories: marketPriceHistoryCitiesHistories,
            }
          : false,
      marketPriceHistoryMansions:
        marketPriceHistoryMansionsHistories !== false
          ? {
              mansionName: apiResults.marketPriceHistoryMansions.mansionName,
              marketPriceHistories: marketPriceHistoryMansionsHistories,
            }
          : false,
      marketPriceHistoryTowns:
        marketPriceHistoryStationsHistories !== false
          ? {
              townName: apiResults.marketPriceHistoryTowns.townName,
              marketPriceHistories: marketPriceHistoryStationsHistories,
            }
          : false,
      marketPriceHistoryStations:
        marketPriceHistoryTownsHistories !== false
          ? {
              stationName: apiResults.marketPriceHistoryStations.stationName,
              marketPriceHistories: marketPriceHistoryTownsHistories,
            }
          : false,
    };

    return convertObject as RentMarketPriceHistoryPropsData;
  }

  function getRentMarketPriceHistoryData(rangeOfYear: number) {
    // console.log("[START! API Data Load]------------------------------------------------------------------------------");
    const apiUrl: string = `${pathRoot}api/v1/rentMarketPriceHistories/?id=${mansionId}&range_of_year=${rangeOfYear}`;

    async function fetchApiData(): Promise<any> {
      const response = await fetch(apiUrl);
      return response.json();
    }

    fetchApiData().then((apiData) => {
      const apiDataJson = convertApiData(camelcaseKeys(apiData, { deep: true }));
      console.log(apiDataJson);
      setMansionRentMarketPriceHistoryState({
        RentMarketPriceHistoryData: apiDataJson,
        averageSquareMeter: averageSquareMeterValue,
        selectedSquareMeter: initialSelectedSquareMeter,
        rangeOfYear,
      });
    });
  }

  // チャートの凡例のboolean値をトグルする関数
  const toggleLegendState = (seriesKey: keyof typeof legendState) => {
    setLegendState((prevState) => ({
      ...prevState,
      [seriesKey]: !prevState[seriesKey],
    }));
  };

  useEffect(() => {
    Highcharts.setOptions({
      lang: {
        thousandsSep: ',',
      },
    });
  }, []);

  const handleChange = (_event: React.SyntheticEvent, newValue: number): void => {
    setTabValue(newValue);
    switch (newValue) {
      case 0:
        setRangeOfYear(1);
        getRentMarketPriceHistoryData(1);
        break;
      case 1:
        setRangeOfYear(3);
        getRentMarketPriceHistoryData(3);
        break;
      case 2:
        setRangeOfYear(5);
        getRentMarketPriceHistoryData(5);
        break;
      default:
        break;
    }
  };
  // 売買相場情報がない場合はnullを返す(項目自体を非表示)
  if (!isRentMarketPriceHistory) return null;

  const [selectedSquareMeter, setSelectedSquareMeter] = useState(70);

  const handleSquareMeterChange = (value: string) => {
    setSelectedSquareMeter(Number(value));
  };
  return (
    <Box
      id="section-rent-market-price-history"
      className="md:max-w-7xl md:w-full md:mx-auto md:px-24 mb-16 max-md:mb-9"
    >
      <RentMarketPriceHistoryProvider mansionRentMarketPriceHistory={mansionRentMarketPriceHistory}>
        <Typography
          variant="h2"
          className="max-md:mx-5 mb-8 max-md:mb-6 pl-4 border-solid border-l-2 border-rf-black relative text-lg font-bold before:absolute before:top-1 before:left-[-16px] max-md:before:left-0"
        >
          このマンションと周辺の賃料相場情報
        </Typography>
        <SquareMetersSelector onSquareMeterChange={handleSquareMeterChange} />
        <Box className="w-full">
          <CustomTabs tabValue={tabValue} handleChange={handleChange} />
          <CustomTabPanel value={tabValue} index={0}>
            {isMobile ? (
              <ChartSP
                legendState={legendState}
                toggleLegendState={toggleLegendState}
                averageSquareMeter={selectedSquareMeter}
              />
            ) : (
              <Chart
                legendState={legendState}
                toggleLegendState={toggleLegendState}
                averageSquareMeter={selectedSquareMeter}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={1}>
            {isMobile ? (
              <ChartSP
                legendState={legendState}
                toggleLegendState={toggleLegendState}
                averageSquareMeter={selectedSquareMeter}
              />
            ) : (
              <Chart
                legendState={legendState}
                toggleLegendState={toggleLegendState}
                averageSquareMeter={selectedSquareMeter}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={2}>
            {isMobile ? (
              <ChartSP
                legendState={legendState}
                toggleLegendState={toggleLegendState}
                averageSquareMeter={selectedSquareMeter}
              />
            ) : (
              <Chart
                legendState={legendState}
                toggleLegendState={toggleLegendState}
                averageSquareMeter={selectedSquareMeter}
              />
            )}
          </CustomTabPanel>
        </Box>
      </RentMarketPriceHistoryProvider>
      <Box className="rf-mansion-review mt-4">
        <Typography className="text-2xs mr-1">※</Typography>
        <Typography className="text-2xs">
          上記の賃料相場情報は
          <Link
            href="https://www.mansion-review.jp/"
            underline="none"
            className="text-rf-blue font-bold underline"
            target="_blank"
          >
            マンションレビュー
          </Link>
          より提供を受けております。過去の募集賃料情報をもとに算出した参考情報であり、実際の取引価格を保証するものではございません。
        </Typography>
      </Box>
    </Box>
  );
}
